import { CommonModule, NgFor, NgIf } from '@angular/common'
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Type,
  ViewChild
} from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { Business } from '../../../../store/business/business.state'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'
import { select, Store } from '@ngrx/store'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
  Subject,
  take,
  takeUntil,
  tap
} from 'rxjs'
import { loadBusinesses } from '../../../../store/business/business.actions'
import { selectBusinesses } from '../../../../store/business/business.reducer'
import dayjs from 'dayjs'
import {
  runningPayrollActions,
  userPayrollActions
} from '../../../../store/payroll/payroll.actions'
import {
  selectRunningPayroll,
  selectUserPayrollData
} from '../../../../store/payroll/payroll.reducers'
import { IncomingPayroll, PayrollStatus } from '../../../../store/payroll/payroll.state'
import { PayslipComponent } from '../payslip/payslip.component'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { RouterModule } from '@angular/router'
import { eps } from '../../../../services/url.service'
import { environment } from '../../../../../environments/environment'
import { selectUser } from '../../../../store/auth/auth.state'
import flatpickr from 'flatpickr'
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect'
@Component({
  selector: 'app-payslip-wrapper',
  imports: [
    NgIf,
    NgFor,
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    CapitalizePipe,
    PayslipComponent
  ],
  templateUrl: './payslip-wrapper.component.html',
  styleUrl: './payslip-wrapper.component.scss'
})
export class PayslipWrapperComponent implements OnInit, OnDestroy, AfterViewInit {
  private store = inject(Store)
  private modalService = inject(NgbModal)
  private destroy$ = new Subject<void>()
  eps = eps
  imageBaseUrl = environment.apiBaseUrl + '/uploads/'
  currentLoggedInUserRole = ''
  currentLoggedInUserId = ''
  currentLoggedInUserBusinessId = ''
  PayslipComponent = PayslipComponent
  runningPayroll$ = this.store.select(selectRunningPayroll)
  // selectedMonth = new FormControl('', { nonNullable: true })
  selectedMonth = new FormControl(dayjs().format('YYYY-MM'), { nonNullable: true })
  payPeriodStartDate = ''
  payPeriodEndDate = ''
  selectedBusiness = new FormControl('', { nonNullable: true })
  businessesList: Business[] = []
  selectedPayrollIndex = new FormControl(null)
  searchTerm = ''
  private searchTerm$ = new BehaviorSubject<string>('')
  filteredpayrolls$!: Observable<IncomingPayroll[]>
  payrolls: IncomingPayroll[] = []
  user: any
  @ViewChild('monthInput') monthInput!: ElementRef
  ngOnInit(): void {
    this.store.dispatch(runningPayrollActions.clearRunningPayroll())
    this.payrolls = []
    this.listenForSelectionChanges()

    this.store
      .select(selectUser)
      .pipe(take(1))
      .subscribe(user => {
        this.user = user
        // this.currentLoggedInUserRole = user?.role.name ?? ''
        // this.currentLoggedInUserRole = user?.userRoles[0].role?.name ?? ''
        if (this.user?.userRoles?.some((ur: any) => ur?.role?.name === 'Admin')) {
          this.currentLoggedInUserRole = 'Admin'
        } else if (this.user?.userRoles?.some((ur: any) => ur?.role?.name === 'Business Owner')) {
          this.currentLoggedInUserRole = 'Business Owner'
        } else {
          this.currentLoggedInUserRole = 'Employee'
        }
        this.currentLoggedInUserId = user?.id ?? ''
        this.currentLoggedInUserBusinessId = user?.businessId ?? ''
        if (
          this.currentLoggedInUserRole === 'Business Owner' ||
          this.currentLoggedInUserRole === 'Admin'
        ) {
          this.loadBusinessesList()
        } else {
          this.selectedBusiness.setValue(this.currentLoggedInUserBusinessId)
        }
        this.setupPayrollDataPipeline()
      })
  }

  // ngAfterViewInit() {
  //   flatpickr(this.monthInput.nativeElement, {
  //     plugins: [
  //       monthSelectPlugin({
  //         shorthand: true,
  //         dateFormat: 'Y-m',
  //         altFormat: 'F Y'
  //       })
  //     ]
  //   })
  // }

  ngAfterViewInit() {
    const today = new Date()
    const maxMonth = new Date(today.getFullYear(), today.getMonth(), 1)

    flatpickr(this.monthInput.nativeElement, {
      plugins: [
        monthSelectPlugin({
          shorthand: true,
          dateFormat: 'Y-m',
          altFormat: 'M Y'
        })
      ],
      maxDate: maxMonth,
      disableMobile: true, // ✅ Safari/iOS UI fix
      clickOpens: true,
      onChange: (selectedDates, dateStr) => {
        this.selectedMonth.setValue(dateStr)
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.searchTerm$.next('') // ✅ important
  }

  // Helper to get employee name initials
  getInitials(firstName = '', lastName = ''): string {
    const firstInitial = firstName.charAt(0)
    const lastInitial = lastName.charAt(0)
    return (firstInitial + lastInitial).toUpperCase()
  }

  private loadBusinessesList(): void {
    this.store.dispatch(loadBusinesses({}))
    this.store
      .pipe(
        select(selectBusinesses),
        filter(businesses => !!businesses && businesses.length > 0),
        takeUntil(this.destroy$)
      )
      .subscribe(businesses => {
        this.businessesList = businesses
        // if (businesses.length === 1) {
        //   this.selectedBusiness.setValue(businesses[0].id)
        // }
        if (this.currentLoggedInUserBusinessId) {
          const currentBusiness = businesses.find(
            business => business.id === this.currentLoggedInUserBusinessId
          )

          if (currentBusiness) {
            this.selectedBusiness.setValue(currentBusiness.id)
          }
        } else if (businesses.length === 1) {
          this.selectedBusiness.setValue(businesses[0].id)
        }
      })
  }

  private listenForSelectionChanges(): void {
    combineLatest([
      this.selectedMonth.valueChanges.pipe(startWith(this.selectedMonth.value)),
      this.selectedBusiness.valueChanges.pipe(startWith(this.selectedBusiness.value))
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([date, businessId]) => !!date && !!businessId),
        distinctUntilChanged((prev, curr) => prev[0] === curr[0] && prev[1] === curr[1]),
        tap(([month, businessId]) => {
          this.selectedPayrollIndex.setValue(null)
          this.payrolls = []

          this.payPeriodStartDate = `${month}-01`
          this.payPeriodEndDate = dayjs(month, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')

          // if (this.currentLoggedInUserRole === 'Employee') {
          //   this.store.dispatch(
          //     userPayrollActions.loadUserPayroll({
          //       payPeriodStart: this.payPeriodStartDate,
          //       payPeriodEnd: this.payPeriodEndDate,
          //       employeeId: this.currentLoggedInUserId
          //     })
          //   )
          // } else {
          //   this.store.dispatch(
          //     runningPayrollActions.loadRunningPayroll({
          //       payPeriodStart: this.payPeriodStartDate,
          //       payPeriodEnd: this.payPeriodEndDate,
          //       belongsTo: businessId
          //     })
          //   )
          // }

          if (
            this.currentLoggedInUserRole === 'Business Owner' ||
            this.currentLoggedInUserRole === 'Admin'
          ) {
            this.store.dispatch(
              runningPayrollActions.loadRunningPayroll({
                payPeriodStart: this.payPeriodStartDate,
                payPeriodEnd: this.payPeriodEndDate,
                belongsTo: businessId
              })
            )
          } else {
            this.store.dispatch(
              userPayrollActions.loadUserPayroll({
                payPeriodStart: this.payPeriodStartDate,
                payPeriodEnd: this.payPeriodEndDate,
                employeeId: this.currentLoggedInUserId
              })
            )
          }
        })
      )
      .subscribe()
  }

  private setupPayrollDataPipeline(): void {
    // 1. Create a source observable based on the user's role
    const payrollsSource$: Observable<IncomingPayroll[]> =
      this.currentLoggedInUserRole === 'Employee'
        ? this.store
            .select(selectUserPayrollData)
            .pipe(
              map(payroll => (payroll && payroll.status === PayrollStatus.PAID ? [payroll] : []))
            )
        : this.store
            .select(selectRunningPayroll)
            .pipe(
              map(runningPayroll =>
                runningPayroll && runningPayroll.status === PayrollStatus.PAID
                  ? (runningPayroll.payrolls ?? [])
                  : []
              )
            )

    // 2. Combine the source with the search term to create the final filtered list
    this.filteredpayrolls$ = combineLatest([
      payrollsSource$,
      this.searchTerm$.pipe(startWith(this.searchTerm)),
      this.selectedMonth.valueChanges.pipe(startWith(this.selectedMonth.value)) // ✅ add this
    ]).pipe(
      takeUntil(this.destroy$),
      // ✅ only allow when month exists
      // filter(([_, __, month]) => !!month),
      map(([payrolls, term, month]) => {
        if (!month) return [] // ✅ no month → no data
        // Keep a copy of the full list for the modal
        this.payrolls = payrolls

        if (!term?.trim()) {
          return payrolls // No search term, return everything
        }
        const lowerCaseTerm = term.toLowerCase()
        return payrolls.filter(payroll =>
          `${payroll.employee.firstName} ${payroll.employee.lastName}`
            .toLowerCase()
            .includes(lowerCaseTerm)
        )
      })
    )
  }

  // openModal(modal: unknown, payroll: IncomingPayroll) {
  //   const modalRef = this.modalService.open(modal, {
  //     scrollable: false,
  //     centered: true,
  //     size: 'lg'
  //   })
  //   if (modalRef.componentInstance) {
  //     modalRef.componentInstance.payrollData = payroll
  //     modalRef.componentInstance.payPeriodStartDate = this.payPeriodStartDate
  //     modalRef.componentInstance.payPeriodEndDate = this.payPeriodEndDate
  //     modalRef.componentInstance.download = true
  //   }
  // }

  openModal(modal: Type<any>, payroll: IncomingPayroll) {
    const modalRef = this.modalService.open(modal, {
      scrollable: false,
      centered: true,
      size: 'xl'
    })

    const instance = modalRef.componentInstance as any

    instance.payrollData = payroll
    instance.payPeriodStartDate = this.payPeriodStartDate
    instance.payPeriodEndDate = this.payPeriodEndDate
    instance.download = true
  }

  onSearchChange(): void {
    this.searchTerm$.next(this.searchTerm)
  }

  private createFilteredObservable(
    source$: Observable<IncomingPayroll[]>
  ): Observable<IncomingPayroll[]> {
    return combineLatest([source$, this.searchTerm$]).pipe(
      map(([payrolls, term]) => {
        if (!term?.trim()) {
          return payrolls // If no search term, return the full list
        }
        const lowerCaseTerm = term.toLowerCase()
        return payrolls.filter(payroll =>
          payroll.employee.firstName.toLowerCase().includes(lowerCaseTerm)
        )
      })
    )
  }
}
